import { Typography } from "@mui/material";
import { Check, Error } from "@mui/icons-material";
import React, { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { Text } from "../../../../common/components/input/Text";
import { makeStyles } from "@mui/styles";
import { parsePhoneNumber } from "libphonenumber-js";
import { LoadingButton } from "@mui/lab";

type MultiFactorAuthInputProps = {
  phone: string;
  wrongCodeError: boolean;
  validateMfaCode: (code: string) => Promise<void>;
  resendCode: () => void;
  isLoading: boolean;
};

const useStyles = makeStyles({
  wrongCode: {
    fontSize: "11px",
    display: "flex",
    gap: "10px",
    marginTop: "10px",
  },
  newCodeSent: {
    fontSize: "11px",
    width: "120px",
    display: "flex",
    gap: "10px",
    marginTop: "10px",
  },
  disclaimerIcon: {
    fontSize: "24px",
  },
});

export const MultiFactorAuthInput: React.FC<MultiFactorAuthInputProps> = ({
  phone,
  wrongCodeError,
  validateMfaCode,
  resendCode,
  isLoading,
}) => {
  const [codeResent, setCodeResent] = useState(false);
  const displayNumber = useMemo(() => {
    if (phone) {
      const num = parsePhoneNumber(phone, "US");
      if (num) {
        return `+${num.countryCallingCode}-***-***-${num.nationalNumber.slice(-4)}`;
      }
    }
    return "the phone number you provided";
  }, [phone]);
  const classes = useStyles();

  return (
    <>
      <div className="fill">
        <p className="text-center fill mt-sm mb-lg w-400">
          Confirm your account by entering the authentication code sent to {displayNumber}.
        </p>
      </div>
      <Form
        onSubmit={async (values) => {
          await validateMfaCode(values.code);
        }}
        render={(props) => (
          <form className="flex-col fill" onSubmit={props.handleSubmit}>
            <div className="flex-col fill">
              <div className="flex-row flex--expand mb-xxs">
                <div className="p-relative">
                  <Text
                    className="m-0 w-300"
                    fullWidth={false}
                    multiline={false}
                    variant="outlined"
                    autoComplete="one-time-code"
                    name="code"
                    field="code"
                    inputMode="numeric"
                    size="small"
                    required
                  />
                  {wrongCodeError && (
                    <Typography color="error" className={classes.wrongCode}>
                      <Error color="error" className={classes.disclaimerIcon} />
                      Incorrect code, please try again
                    </Typography>
                  )}
                  {!wrongCodeError && codeResent && (
                    <Typography color="green" className={classes.newCodeSent}>
                      <Check color="success" className={classes.disclaimerIcon} />
                      New code sent
                    </Typography>
                  )}
                </div>
              </div>
              <div className="fill mb-xxs">
                <Typography fontSize="small" className="my-0 w-300 text-right">
                  Didn't get it?&nbsp;
                  <a
                    href="#"
                    onClick={() => {
                      resendCode();
                      setCodeResent(true);
                    }}
                  >
                    Resend Code
                  </a>
                </Typography>
              </div>
              <div className="flex-row flex--expand" style={{ marginTop: "12px" }}>
                <LoadingButton
                  variant="contained"
                  color="primary"
                  size="large"
                  className="text-transform-none w-300"
                  disabled={!/^[0-9]{6}$/.test(props.values?.code)}
                  onClick={props.handleSubmit}
                  loading={isLoading}
                  type="submit"
                >
                  Submit
                </LoadingButton>
              </div>
            </div>
          </form>
        )}
      />
    </>
  );
};
